import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { X } from "lucide-react";

interface EditSubscriptionTransactionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: any | null;
  tenantId: string;
  tenantName: string;
  onSuccess: () => void;
}

export function EditSubscriptionTransactionModal({
  open,
  onOpenChange,
  transaction,
  tenantId,
  tenantName,
  onSuccess,
}: EditSubscriptionTransactionModalProps) {
  const [paymentType, setPaymentType] = useState<string>("regular");
  const [paymentDate, setPaymentDate] = useState<string>("");
  const [expirationDate, setExpirationDate] = useState<string>("");
  const [isLifetime, setIsLifetime] = useState(false);
  const [observations, setObservations] = useState("");

  const queryClient = useQueryClient();

  // Inicializar campos quando a transação mudar
  useEffect(() => {
    if (transaction && open) {
      setPaymentType(transaction.payment_type || "regular");
      setPaymentDate(
        transaction.payment_date
          ? format(parseISO(transaction.payment_date), "yyyy-MM-dd")
          : ""
      );
      setExpirationDate(
        transaction.expiration_date
          ? format(parseISO(transaction.expiration_date), "yyyy-MM-dd")
          : ""
      );
      setIsLifetime(!transaction.expiration_date);
      setObservations(
        transaction.metadata?.observations || transaction.metadata?.note || ""
      );
    }
  }, [transaction, open]);

  const updateTransactionMutation = useMutation({
    mutationFn: async () => {
      if (!transaction) throw new Error("Transação não selecionada");

      const updateData: any = {};

      // Atualizar payment_type se mudou
      if (transaction.payment_type !== paymentType) {
        updateData.payment_type = paymentType === "regular" ? null : paymentType;
        updateData.payment_method = paymentType === "regular" ? null : paymentType;
      }

      // Atualizar payment_date
      if (paymentDate) {
        const paymentDateObj = new Date(paymentDate);
        paymentDateObj.setHours(0, 0, 0, 0);
        updateData.payment_date = paymentDateObj.toISOString();
      }

      // Atualizar expiration_date
      if (isLifetime) {
        updateData.expiration_date = null;
      } else if (expirationDate) {
        const expirationDateObj = new Date(expirationDate);
        expirationDateObj.setHours(23, 59, 59, 999);
        updateData.expiration_date = expirationDateObj.toISOString();
      }

      // Atualizar metadata (observations)
      const currentMetadata = transaction.metadata || {};
      const updatedMetadata = {
        ...currentMetadata,
        observations: observations || undefined,
        note: observations || undefined,
      };

      // Remover campos undefined
      Object.keys(updatedMetadata).forEach((key) => {
        if (updatedMetadata[key] === undefined) {
          delete updatedMetadata[key];
        }
      });

      updateData.metadata = updatedMetadata;

      // Atualizar transação
      const { error } = await supabase
        .from("subscription_transactions")
        .update(updateData)
        .eq("id", transaction.id);

      if (error) throw error;

      // Se a transação editada é a mais recente e ativa, atualizar também o tenant
      // (opcional, mas pode ser necessário para manter consistência)
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: allTransactions } = await supabase
          .from("subscription_transactions")
          .select("*")
          .eq("tenant_id", tenantId)
          .eq("status", "approved")
          .order("payment_date", { ascending: false });

        if (allTransactions && allTransactions.length > 0) {
          const mostRecent = allTransactions[0];
          if (mostRecent.id === transaction.id) {
            // Esta é a transação mais recente, atualizar tenant
            const tenantUpdate: any = {
              subscription_last_payment_date: updateData.payment_date || transaction.payment_date,
            };

            if (updateData.expiration_date !== undefined) {
              tenantUpdate.subscription_next_billing_date = updateData.expiration_date;
            }

            await supabase
              .from("tenants")
              .update(tenantUpdate)
              .eq("id", tenantId);
          }
        }
      }
    },
    onSuccess: () => {
      toast.success("Transação atualizada com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["tenant-subscription-transactions", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
      onSuccess();
      onOpenChange(false);
    },
    onError: (error: any) => {
      console.error("Erro ao atualizar transação:", error);
      toast.error(
        error.message || "Erro ao atualizar transação. Tente novamente."
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateTransactionMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader className="relative">
          <DialogTitle>Editar Transação de Assinatura</DialogTitle>
          <DialogDescription>
            Edite os dados da transação para {tenantName}
          </DialogDescription>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="paymentType">Tipo de Pagamento</Label>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger id="paymentType">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="cortesia">Cortesia</SelectItem>
                  <SelectItem value="testers">Beta Testers</SelectItem>
                  <SelectItem value="promotion">Promoção</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentDate">Data de Pagamento</Label>
              <Input
                id="paymentDate"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isLifetime"
                  checked={isLifetime}
                  onChange={(e) => {
                    setIsLifetime(e.target.checked);
                    if (e.target.checked) {
                      setExpirationDate("");
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="isLifetime" className="cursor-pointer">
                  Para sempre (sem data de expiração)
                </Label>
              </div>
            </div>

            {!isLifetime && (
              <div className="space-y-2">
                <Label htmlFor="expirationDate">Data de Expiração</Label>
                <Input
                  id="expirationDate"
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  required={!isLifetime}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="observations">Observações</Label>
              <Textarea
                id="observations"
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                placeholder="Adicione observações sobre esta transação..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={updateTransactionMutation.isPending}
            >
              {updateTransactionMutation.isPending
                ? "Salvando..."
                : "Salvar Alterações"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

